import classNames from "classnames";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import EventSummaryItem from "../../../stores/domainStore/pages/models/eventSummaryItem";
import EventTimeFormat from "../../atoms/eventTimeFormat/eventTimeFormat";
import "./eventSummary.sass";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import EventIcon from "../eventIcon/eventIcon";
import {utcToZonedTime} from "date-fns-tz";
import {useStores} from "../../../stores/useStores";

export interface IEventSummaryProps {
  data: EventSummaryItem;
  calendarScheduleStartDate?: Date;
}

const EventSummary: React.FC<IEventSummaryProps> = ({
  data,
  calendarScheduleStartDate
}) => {
  const {
    uiStore: {eventsUiStore}
  } = useStores();

  const [timeZone] = useState(eventsUiStore.getTimeZone());
  const now = utcToZonedTime(new Date(), timeZone);
  const releaseDateLocal =
    data.extendedProps.releaseDate &&
    utcToZonedTime(data.extendedProps.releaseDate, timeZone);

  const {t} = useTranslation(["event"]);

  const isVirtualEvent = data.location === t("event:labels.virtualEventSelect");
  const isPastEvent = data.end! < utcToZonedTime(new Date(), data.timeZone);
  const isFeaturedEvent = data.extendedProps.isFeaturedEvent;
  const isBookableEvent = !isPastEvent && data.extendedProps.isBookableEvent;
  const isPreReleaseEvent =
    data.extendedProps.isBookableEvent && releaseDateLocal !== undefined;
  const isPreReleaseInPastAndEventInUnreleasedPeriod =
    isPreReleaseEvent &&
    releaseDateLocal < now &&
    data.extendedProps.eventStartDateTimeOffSet! >=
      calendarScheduleStartDate?.toISOString()!;
  const isPreReleaseInPastAndEventInReleasedPeriod =
    isPreReleaseEvent &&
    releaseDateLocal < now &&
    data.extendedProps.eventStartDateTimeOffSet! <
      calendarScheduleStartDate?.toISOString()!;
  const isMultiVillageEvent = data.extendedProps.isMultiVillageEvent;
  const locationText = isMultiVillageEvent
    ? [data.location.trim(), t("translation:labels:residentExperienceTeam")]
        .filter(Boolean)
        .join(" | ")
    : data.location;
  const isCancelledOrPostponed =
    EventDetailsHelper.isCancelledOrPostponedStatus(
      data.extendedProps.eventStatus
    );

  return (
    <div
      className={classNames("eventSummary", {
        "is-past": isPastEvent,
        "virtual-event": isVirtualEvent,
        "cancelled-postponed-event": isCancelledOrPostponed,
        "bookable-event": isBookableEvent,
        "non-bookable-event": !isBookableEvent,
        "no-remaining-capacity":
          data.extendedProps.isBookableEvent && data.isFullyBooked,
        "featured-event": isFeaturedEvent,
        "pre-release-event": isPreReleaseEvent,
        "pre-release-in-past-event-in-unreleased-period":
          isPreReleaseInPastAndEventInUnreleasedPeriod,
        "pre-release-in-past-event-in-released-period":
          isPreReleaseInPastAndEventInReleasedPeriod
      })}
      data-testid="eventSummary"
    >
      <div className={"eventSummaryTime"}>
        <div>
          {data.allDay ? (
            t("event:labels.calendar.allDay")
          ) : (
            <EventTimeFormat time={data.start} />
          )}
        </div>
      </div>
      <div className={"summaryText"}>
        <div>
          <div>{data.title}</div>
          <EventIcon
            isBookableEvent={data.extendedProps.isBookableEvent}
            isCancelledOrPostponedEvent={isCancelledOrPostponed}
            isFeaturedEvent={isFeaturedEvent}
            isPastEvent={isPastEvent}
            isVirtualEvent={isVirtualEvent}
            isPreReleaseEvent={isPreReleaseEvent}
            isPreReleaseInPastAndEventInUnreleasedPeriod={
              isPreReleaseInPastAndEventInUnreleasedPeriod
            }
            isPreReleaseInPastAndEventInReleasedPeriod={
              isPreReleaseInPastAndEventInReleasedPeriod
            }
            recurringStatus={data.extendedProps.recurringStatus!}
          />
        </div>
        {eventsUiStore.isEnhancedCalendar && <div>{locationText}</div>}
      </div>
    </div>
  );
};

export default EventSummary;
